import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { getCroppedAvatarFile } from "../../../utils/cropImage";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import "./AvatarCropModal.css";

/**
 * Inline crop panel — renders inside the Edit Avatar card (same SettingsModalShell).
 * Parent calls `ref.current.applyCrop()` from the footer "Use photo" button.
 */
const AvatarCropPanel = forwardRef(function AvatarCropPanel({ imageSrc, busy }, ref) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  }, [imageSrc]);

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      canApply: () => Boolean(imageSrc && croppedAreaPixels && !busy),
      applyCrop: async () => {
        if (!imageSrc || !croppedAreaPixels || busy) {
          throw new Error("Crop not ready");
        }
        try {
          const file = await getCroppedAvatarFile(imageSrc, croppedAreaPixels, {
            rotation,
            outputSize: 512,
            mime: "image/jpeg",
            quality: 0.88,
            fileName: "avatar.jpg",
          });
          const previewUrl = URL.createObjectURL(file);
          return { file, previewUrl };
        } catch (err) {
          alertErrorMessage("Could not crop this image. Try another photo.");
          throw err;
        }
      },
    }),
    [imageSrc, croppedAreaPixels, busy, rotation]
  );

  if (!imageSrc) return null;

  return (
    <div className="avatar-crop-inline">
      <div className="settings-modal__info-banner avatar-crop-inline__hint" role="status">
        <i className="settings-modal__info-icon ri-information-fill" aria-hidden="true" />
        <p>Drag to reposition. Adjust zoom and rotate, then tap Use photo.</p>
      </div>

      <div className="avatar-crop-inline__stage">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={1}
          cropShape="round"
          showGrid={false}
          objectFit="contain"
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
        />
      </div>

      <div className="avatar-crop-inline__controls">
        <label className="avatar-crop-inline__control">
          <span className="avatar-crop-inline__label">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={busy}
            aria-label="Zoom"
          />
        </label>
        <label className="avatar-crop-inline__control">
          <span className="avatar-crop-inline__label">Rotate</span>
          <input
            type="range"
            min={-45}
            max={45}
            step={1}
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            disabled={busy}
            aria-label="Rotate"
          />
        </label>
        <button
          type="button"
          className="avatar-crop-inline__chip"
          disabled={busy || rotation === 0}
          onClick={() => setRotation(0)}
        >
          Reset rotation
        </button>
      </div>
    </div>
  );
});

export default AvatarCropPanel;
